import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme } from '../contexts/ThemeContext'
import { db } from '../services/firebase'
import { collection, getDocs, query, where, limit, doc, updateDoc } from 'firebase/firestore'
import {
  HomeIcon,
  CalendarIcon,
  BellIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
  BellSlashIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import logo from '../assets/logo.png'
import { resolveNotificationMessage } from '../utils/notificationMessage'

const UserLayout = () => {
  const { t } = useTranslation()
  const { user, userData, logout } = useAuth()
  const { currentLanguage, changeLanguage } = useLanguage()
  const { darkMode, toggleDarkMode, setDarkModePreference } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [allNotificationsOpen, setAllNotificationsOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [notificationsError, setNotificationsError] = useState('')
  const notificationsRef = useRef(null)
  const languageRef = useRef(null)

  const navigation = [
    { id: 'home', name: t('nav.home'), href: '/', icon: HomeIcon },
    { id: 'matches', name: t('nav.matches'), href: '/matches', icon: CalendarIcon },
    { id: 'centers', name: t('nav.centers'), href: '/centers', icon: BuildingOfficeIcon },
    { id: 'createMatch', name: t('home.createMatch'), href: '/create-match', icon: CalendarIcon },
  ]

  const mobileSidebarNavigation = [
    { id: 'home', name: t('nav.home'), href: '/', icon: HomeIcon },
    { id: 'matches', name: t('nav.matches'), href: '/matches', icon: CalendarIcon },
    { id: 'centers', name: t('nav.centers'), href: '/centers', icon: BuildingOfficeIcon },
    { id: 'createMatch', name: t('home.createMatch'), href: '/create-match', icon: CalendarIcon },
    { id: 'search', name: t('nav.search'), href: '/search', icon: MagnifyingGlassIcon },
  ]

  const handleLogout = async () => {
    await logout()
    setDarkModePreference(true)
    navigate('/login')
  }

  const unreadCount = useMemo(
    () => notifications.filter((item) => item.read === false).length,
    [notifications]
  )

  const latestNotifications = useMemo(() => notifications.slice(0, 10), [notifications])
  const currentUid = useMemo(() => user?.uid || userData?.uid || userData?.id || null, [user, userData])
  const profileInitials = useMemo(() => {
    const first = String(userData?.firstName || '').trim()
    const last = String(userData?.lastName || '').trim()
    const displayName = String(userData?.displayName || user?.displayName || '').trim()
    const email = String(userData?.email || user?.email || '').trim()

    if (first || last) {
      const firstInitial = first ? first[0].toUpperCase() : ''
      const lastInitial = last ? last[0].toUpperCase() : ''
      return `${firstInitial}${lastInitial}` || 'U'
    }

    if (displayName) {
      const parts = displayName.split(/\s+/).filter(Boolean)
      const firstInitial = parts[0]?.[0]?.toUpperCase() || ''
      const lastInitial = parts[1]?.[0]?.toUpperCase() || ''
      return `${firstInitial}${lastInitial}` || firstInitial || 'U'
    }

    if (email) {
      return email[0].toUpperCase()
    }

    return 'U'
  }, [userData, user])

  const getCreatedAtMillis = (value) => {
    if (!value) return 0
    if (typeof value?.toDate === 'function') return value.toDate().getTime()
    const asDate = new Date(value)
    return Number.isNaN(asDate.getTime()) ? 0 : asDate.getTime()
  }

  const formatCreatedAt = (value) => {
    const millis = getCreatedAtMillis(value)
    if (!millis) return ''
    return new Date(millis).toLocaleString()
  }

  const resolveNotificationPath = (notification) => {
    const directPath = notification?.path || notification?.link || notification?.url
    if (typeof directPath === 'string' && directPath.trim()) {
      return directPath.trim().startsWith('/') ? directPath.trim() : `/${directPath.trim()}`
    }

    if (notification?.matchId) {
      return `/match/${notification.matchId}`
    }

    if (notification?.type === 'follow') {
      const profileUserId = notification?.senderId || notification?.actorId || notification?.fromUserId || notification?.userId
      if (profileUserId) {
        return `/profile/${profileUserId}`
      }
    }

    return '/notifications'
  }

  const markNotificationAsRead = async (notification) => {
    if (!notification || notification.source !== 'notifications' || notification.read === true) {
      return
    }

    try {
      await updateDoc(doc(db, 'notifications', notification.id), { read: true })
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id && item.source === notification.source
          ? { ...item, read: true }
          : item
      )))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleNotificationClick = async (notification) => {
    await markNotificationAsRead(notification)
    setNotificationsOpen(false)
    setAllNotificationsOpen(false)
    navigate(resolveNotificationPath(notification))
  }

  const fetchHeaderNotifications = async () => {
    if (!currentUid) {
      setNotifications([])
      return
    }

    setNotificationsLoading(true)
    setNotificationsError('')

    try {
      const loadUserNotifications = async () => {
        try {
          const [byUserId, byRecipientId, byTargetAll, byUserIds] = await Promise.all([
            getDocs(
              query(
                collection(db, 'notifications'),
                where('userId', '==', currentUid),
                limit(50)
              )
            ),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('recipientId', '==', currentUid),
                limit(50)
              )
            ).catch(() => ({ docs: [] })),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('target', '==', 'all'),
                limit(50)
              )
            ).catch(() => ({ docs: [] })),
            getDocs(
              query(
                collection(db, 'notifications'),
                where('userIds', 'array-contains', currentUid),
                limit(50)
              )
            ).catch(() => ({ docs: [] }))
          ])

          const docMap = new Map()
          ;[byUserId, byRecipientId, byTargetAll, byUserIds].forEach((snapshot) => {
            snapshot.docs.forEach((notificationDoc) => {
              docMap.set(notificationDoc.id, notificationDoc)
            })
          })

          return Array.from(docMap.values())
        } catch (queryError) {
          console.warn('Using fallback notifications query due to index issue:', queryError)
          try {
            const fallbackUserNotificationsQuery = query(
              collection(db, 'notifications'),
              where('userId', '==', currentUid),
              limit(200)
            )
            const snapshot = await getDocs(fallbackUserNotificationsQuery)
            return snapshot.docs
          } catch (fallbackError) {
            console.error('Unable to load notifications collection:', fallbackError)
            return []
          }
        }
      }

      const loadPushNotifications = async () => {
        try {
          const [allPushSnapshot, directPushSnapshot, listPushSnapshot] = await Promise.all([
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('target', '==', 'all'),
                limit(50)
              )
            ),
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('userId', '==', currentUid),
                limit(50)
              )
            ),
            getDocs(
              query(
                collection(db, 'pushNotifications'),
                where('userIds', 'array-contains', currentUid),
                limit(50)
              )
            ).catch(() => ({ docs: [] }))
          ])

          return [
            ...allPushSnapshot.docs,
            ...directPushSnapshot.docs,
            ...listPushSnapshot.docs
          ]
        } catch (queryError) {
          if (queryError?.code === 'permission-denied') {
            console.warn('pushNotifications read denied by Firestore rules; skipping push notifications in header.')
            return []
          }

          console.warn('Using fallback pushNotifications query due to index issue:', queryError)
          try {
            const fallbackPushSnapshot = await getDocs(
              query(collection(db, 'pushNotifications'), limit(200))
            )

            return fallbackPushSnapshot.docs.filter((notificationDoc) => {
              const data = notificationDoc.data()
              if (data.target === 'all') return true
              if (data.userId && data.userId === currentUid) return true
              if (Array.isArray(data.userIds) && data.userIds.includes(currentUid)) return true
              return false
            })
          } catch (fallbackError) {
            console.error('Unable to load pushNotifications collection:', fallbackError)
            return []
          }
        }
      }

      const [userNotificationDocs, pushNotificationDocs] = await Promise.all([
        loadUserNotifications(),
        loadPushNotifications()
      ])

      const userNotifications = userNotificationDocs.map((notificationDoc) => ({
        id: notificationDoc.id,
        source: 'notifications',
        ...notificationDoc.data()
      }))

      const pushMap = new Map()
      pushNotificationDocs.forEach((notificationDoc) => {
        const key = notificationDoc.id
        if (!pushMap.has(key)) {
          pushMap.set(key, {
            id: key,
            source: 'pushNotifications',
            read: true,
            ...notificationDoc.data()
          })
        }
      })

      const relevantPushNotifications = Array.from(pushMap.values())

      let mergedNotifications = [...userNotifications, ...relevantPushNotifications]
        .filter((item) => {
          // Exclude deleted notifications
          if (item.deleted === true) return false
          
          // Exclude notifications where senderId equals current user
          const senderIdStr = String(item.senderId || '')
          const currentUidStr = String(currentUid || '')
          const shouldExclude = senderIdStr.length > 0 && senderIdStr === currentUidStr
          
          if (shouldExclude) {
            return false
          }
          
          // Accept if has message content OR has messageI18nKey (i18n-based notification)
          const hasMessage = typeof item.message === 'string' && item.message.trim().length > 0
          const hasI18nKey = typeof item.messageI18nKey === 'string' && item.messageI18nKey.trim().length > 0
          return hasMessage || hasI18nKey
        })
        .sort((a, b) => getCreatedAtMillis(b.createdAt) - getCreatedAtMillis(a.createdAt))

      setNotifications(mergedNotifications)
    } catch (error) {
      console.error('Error fetching header notifications:', error)
      setNotificationsError(error?.message || t('notifications.failedToFetch'))
      setNotifications([])
    } finally {
      setNotificationsLoading(false)
    }
  }

  const toggleNotifications = (notificationOrEvent) => {
    const isDOMEvent = notificationOrEvent && typeof notificationOrEvent === 'object' && 'target' in notificationOrEvent
    const isNotificationClick = notificationOrEvent && typeof notificationOrEvent === 'object' && notificationOrEvent !== null && !isDOMEvent && (notificationOrEvent.matchId || notificationOrEvent.type)
    const willOpen = isNotificationClick ? false : !notificationsOpen
    setNotificationsOpen(willOpen)
    if (willOpen) {
      setLanguageOpen(false)
      fetchHeaderNotifications()
    }

    if (isNotificationClick && notificationOrEvent.matchId) {
      setNotificationsOpen(false)
      if (notificationOrEvent.type === 'chat_message') {
        navigate(`/match/${notificationOrEvent.matchId}?chat=true`)
      } else {
        navigate(`/match/${notificationOrEvent.matchId}`)
      }
    } else if (isNotificationClick && notificationOrEvent.type === 'follow' && notificationOrEvent.senderId) {
      setNotificationsOpen(false)
      navigate(`/profile/${notificationOrEvent.senderId}`)
    }
  }

  useEffect(() => {
    if (!currentUid) {
      setNotifications([])
      setNotificationsError('')
      setNotificationsLoading(false)
      return
    }

    fetchHeaderNotifications()

    const refreshIntervalId = window.setInterval(() => {
      fetchHeaderNotifications()
    }, 30000)

    return () => {
      window.clearInterval(refreshIntervalId)
    }
  }, [currentUid])

  useEffect(() => {
    const refreshNotifications = () => {
      fetchHeaderNotifications()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchHeaderNotifications()
      }
    }

    window.addEventListener('giochiamo:notification-received', refreshNotifications)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('giochiamo:notification-received', refreshNotifications)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [currentUid])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsOpen && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false)
      }

      if (languageOpen && languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [notificationsOpen, languageOpen])

  const surfaceClass = darkMode ? 'bg-sport-darker text-sport-light' : 'bg-gray-50 text-gray-900'
  const headerClass = darkMode
    ? 'nav-glass'
    : 'bg-white/90 backdrop-blur border-b border-gray-200 shadow-sm'
  const popoverClass = darkMode
    ? 'card-sport bg-sport-darker/95 sm:bg-sport-darker/90 border border-sport-silver/20'
    : 'bg-white border border-gray-200 text-gray-900'
  const popoverMutedTextClass = darkMode ? 'text-sport-silver/60' : 'text-gray-500'
  const popoverTitleClass = darkMode ? 'text-sport-light' : 'text-gray-900'
  const sidebarClass = darkMode
    ? 'bg-sport-darker border-r border-sport-silver/20'
    : 'bg-white border-r border-gray-200'
  const modalCardClass = darkMode
    ? 'card-sport border border-sport-silver/20'
    : 'bg-white border border-gray-200'
  const navInactiveClass = darkMode
    ? 'text-sport-silver hover:text-sport-primary hover:bg-sport-primary/10'
    : 'text-gray-600 hover:text-sport-primary hover:bg-gray-100'
  const iconButtonClass = darkMode
    ? 'text-sport-silver hover:text-sport-primary hover:bg-sport-primary/10'
    : 'text-gray-600 hover:text-sport-primary hover:bg-gray-100'
  const languageToggleClass = darkMode
    ? 'border-sport-primary/30 bg-sport-darker/90 text-sport-light'
    : 'border-gray-300 bg-white text-gray-700'
  const mobilePlayNowClass = darkMode
    ? 'inline-flex md:hidden items-center gap-1 rounded-lg border border-cyan-300/65 bg-gradient-to-r from-cyan-500/25 via-sky-400/20 to-fuchsia-500/25 px-2 py-1 text-[9px] font-[\'Orbitron\'] uppercase tracking-[0.1em] text-cyan-100 shadow-[0_0_12px_rgba(34,211,238,0.28)] transition hover:border-cyan-200 hover:shadow-[0_0_18px_rgba(34,211,238,0.38)] sm:px-2.5 sm:tracking-[0.12em]'
    : 'inline-flex md:hidden items-center gap-1 rounded-lg border border-sport-primary/35 bg-gradient-to-r from-sport-primary/15 via-sky-100 to-sport-secondary/15 px-2 py-1 text-[9px] font-[\'Orbitron\'] uppercase tracking-[0.1em] text-sport-dark shadow-[0_0_8px_rgba(14,165,233,0.14)] transition hover:border-sport-primary/55 hover:shadow-[0_0_12px_rgba(14,165,233,0.2)] sm:px-2.5 sm:tracking-[0.12em]'
  const mobilePlayNowIconClass = 'h-3.5 w-3.5 object-contain'
  const desktopPlayNowClass = darkMode
    ? 'inline-flex items-center gap-1.5 rounded-xl border border-cyan-300/65 bg-gradient-to-r from-cyan-500/25 via-sky-400/20 to-fuchsia-500/25 px-4 py-2 text-xs font-[\'Orbitron\'] uppercase tracking-[0.1em] text-cyan-100 shadow-[0_0_16px_rgba(34,211,238,0.32)] transition hover:border-cyan-200 hover:shadow-[0_0_22px_rgba(34,211,238,0.4)]'
    : 'inline-flex items-center gap-1.5 rounded-xl border border-sport-primary/35 bg-gradient-to-r from-sport-primary/15 via-sky-100 to-sport-secondary/15 px-4 py-2 text-xs font-[\'Orbitron\'] uppercase tracking-[0.1em] text-sport-dark shadow-[0_0_10px_rgba(14,165,233,0.16)] transition hover:border-sport-primary/55 hover:shadow-[0_0_14px_rgba(14,165,233,0.22)]'
  const desktopPlayNowActiveClass = darkMode
    ? 'border-cyan-200/90 shadow-[0_0_24px_rgba(34,211,238,0.45)]'
    : 'border-sport-primary/65 shadow-[0_0_16px_rgba(14,165,233,0.28)]'
  const desktopPlayNowIconClass = 'h-4 w-4 object-contain'
  const brandWordmarkClass = "font-['Orbitron'] font-black italic uppercase leading-none text-white drop-shadow-[0_2px_0_rgba(11,18,32,0.9)]"
  const brandWordmarkStyle = {
    WebkitTextStroke: '1px rgba(13, 27, 55, 0.9)',
    textShadow: '0 2px 0 rgba(13,27,55,0.95), 0 0 16px rgba(161, 232, 255, 0.25)'
  }

  return (
    <div className={`min-h-screen ${surfaceClass}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sport-primary/5 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-sport-secondary/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-50 ${headerClass}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 items-center justify-between gap-1.5 py-2 sm:min-h-20 sm:gap-3 sm:py-0">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`shrink-0 md:hidden p-1.5 rounded-xl transition ${iconButtonClass}`}
              >
                {sidebarOpen ? (
                  <XMarkIcon className="h-5 w-5" />
                ) : (
                  <Bars3Icon className="h-5 w-5" />
                )}
              </button>

              {/* Mobile: home control — colors follow sport theme + light/dark */}
              <Link
                to="/"
                className={[
                  "group relative flex md:hidden h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 font-['Orbitron'] transition-all duration-300 active:scale-[0.97]",
                  location.pathname === '/'
                    ? darkMode
                      ? 'border-sport-primary bg-sport-primary/15 shadow-[0_0_20px_rgba(0,212,255,0.42),inset_0_0_18px_rgba(0,212,255,0.1)] ring-1 ring-sport-primary/40'
                      : 'border-sport-primary bg-sport-primary/10 shadow-[0_0_16px_rgba(0,212,255,0.28),inset_0_0_12px_rgba(0,212,255,0.12)] ring-1 ring-sport-primary/30'
                    : darkMode
                      ? 'border-sport-silver/25 bg-sport-darker/95 shadow-[0_0_8px_rgba(0,212,255,0.12)] hover:border-sport-primary/60 hover:bg-sport-primary/5 hover:shadow-[0_0_18px_rgba(0,212,255,0.3)]'
                      : 'border-gray-300/90 bg-white/95 shadow-sm hover:border-sport-primary/50 hover:bg-sport-primary/[0.07] hover:shadow-[0_0_14px_rgba(0,212,255,0.22)]'
                ].join(' ')}
                aria-label={t('nav.home')}
              >
                <span
                  className={
                    darkMode
                      ? 'pointer-events-none absolute inset-0 bg-gradient-to-br from-sport-primary/20 via-transparent to-sport-secondary/10 opacity-90 transition-opacity group-hover:opacity-100'
                      : 'pointer-events-none absolute inset-0 bg-gradient-to-br from-sport-primary/12 via-transparent to-sport-secondary/8 opacity-90 transition-opacity group-hover:opacity-100'
                  }
                  aria-hidden
                />
                <span
                  className="pointer-events-none absolute -inset-px rounded-lg bg-gradient-to-r from-sport-primary/20 via-sport-secondary/15 to-sport-primary/20 opacity-0 blur-[1px] transition group-hover:opacity-100"
                  aria-hidden
                />
                <HomeIcon
                  className={[
                    'relative h-5 w-5 transition-transform group-hover:scale-110',
                    location.pathname === '/'
                      ? darkMode
                        ? 'text-sport-light drop-shadow-[0_0_8px_rgba(0,212,255,0.55)]'
                        : 'text-sport-primary drop-shadow-[0_0_6px_rgba(0,212,255,0.35)]'
                      : darkMode
                        ? 'text-sport-primary/95 drop-shadow-[0_0_4px_rgba(0,212,255,0.25)]'
                        : 'text-sport-primary drop-shadow-[0_0_2px_rgba(0,212,255,0.2)]'
                  ].join(' ')}
                />
              </Link>

              {/* Desktop: logo + wordmark */}
              <Link to="/" className="group hidden min-w-0 flex-1 items-center gap-3 sm:gap-3 md:flex">
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                  className="relative shrink-0"
                >
                  <img
                    src={logo}
                    alt={t('app.brand')}
                    className="h-14 w-14 object-contain drop-shadow-lg ring-glow rounded-full"
                  />
                  <div className="absolute inset-0 bg-sport-primary/20 rounded-full blur-xl group-hover:bg-sport-primary/40 transition-all duration-300"></div>
                </motion.div>
                <span
                  className={`${brandWordmarkClass} -skew-x-6 min-w-0 truncate text-3xl leading-tight tracking-[0.06em]`}
                  style={brandWordmarkStyle}
                >
                  {t('app.brand').toUpperCase()}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                const isCreateMatch = item.id === 'createMatch'
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-4 py-2 rounded-xl transition-all duration-300 ${
                      isCreateMatch
                        ? `${desktopPlayNowClass} ${isActive ? desktopPlayNowActiveClass : ''}`
                        : isActive
                          ? 'bg-sport-primary/20 text-sport-primary border border-sport-primary/30'
                          : navInactiveClass
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {isCreateMatch ? <img src={logo} alt="" aria-hidden className={desktopPlayNowIconClass} /> : <item.icon className="h-5 w-5" />}
                      <span>{item.name}</span>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Right Actions */}
            <div className="flex shrink-0 items-center gap-1 sm:gap-2 md:gap-3">
              <Link
                to="/create-match"
                className={`${mobilePlayNowClass} shrink-0`}
              >
                <img src={logo} alt="" aria-hidden className={`${mobilePlayNowIconClass} shrink-0`} />
                <span className="whitespace-nowrap">{t('home.createMatch')}</span>
              </Link>

              {/* Notifications */}
              <div ref={notificationsRef} className="relative">
                <button
                  onClick={toggleNotifications}
                  className={`p-1.5 sm:p-2 rounded-xl transition relative ${iconButtonClass}`}
                >
                  <BellIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 min-w-[1rem] h-4 px-1 bg-sport-secondary rounded-full text-[9px] leading-4 text-sport-dark font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {notificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`absolute right-0 mt-2 w-80 shadow-2xl z-50 ${popoverClass}`}
                    >
                      <div className={`p-4 border-b ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <h3 className={`font-semibold ${popoverTitleClass}`}>{t('notifications.title')}</h3>
                          <button
                            type="button"
                            onClick={() => setNotificationsOpen(false)}
                            className={`rounded-lg p-1 transition ${darkMode ? 'text-sport-silver hover:text-sport-light hover:bg-sport-primary/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                            aria-label="Close notifications"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notificationsLoading ? (
                          <div className={`p-4 text-center ${popoverMutedTextClass}`}>{t('common.loading')}</div>
                        ) : notificationsError ? (
                          <div className={`p-4 text-center text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                            {notificationsError}
                            <div className={`mt-1 ${popoverMutedTextClass}`}>UID: {currentUid || 'not-authenticated'}</div>
                          </div>
                        ) : latestNotifications.length > 0 ? (
                          <div className={`divide-y ${darkMode ? 'divide-sport-silver/10' : 'divide-gray-100'}`}>
                            {latestNotifications.map((notification) => (
                              <button
                                type="button"
                                key={`${notification.source}-${notification.id}`}
                                onClick={() => handleNotificationClick(notification)}
                                className={`w-full p-3 text-left transition ${darkMode ? 'hover:bg-sport-primary/5' : 'hover:bg-gray-50'} ${notification.read ? 'opacity-80' : ''}`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className={`text-sm break-words ${popoverTitleClass} ${notification.read ? 'font-normal' : 'font-semibold'}`}>
                                      {resolveNotificationMessage(t, notification)}
                                    </p>
                                    <div className={`mt-1 flex items-center justify-between text-[11px] ${popoverMutedTextClass}`}>
                                      <span>{notification.source === 'pushNotifications' ? 'Broadcast' : 'Personal'}</span>
                                      <span>{formatCreatedAt(notification.createdAt)}</span>
                                    </div>
                                  </div>
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${notification.read ? (darkMode ? 'bg-white/5 text-sport-silver/70' : 'bg-gray-100 text-gray-500') : (darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-100 text-blue-700')}`}>
                                    {notification.read ? t('notifications.read', { defaultValue: 'Read' }) : t('notifications.unread', { defaultValue: 'Unread' })}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className={`p-4 text-center ${popoverMutedTextClass}`}>
                            {t('notifications.noNotifications')}
                            <div className="mt-1 text-[11px]">UID: {currentUid || 'not-authenticated'}</div>
                          </div>
                        )}
                      </div>
                      {!notificationsLoading && notifications.length > 0 && (
                        <div className={`border-t p-2 ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                          <button
                            onClick={() => {
                              setNotificationsOpen(false)
                              setAllNotificationsOpen(true)
                            }}
                            className="w-full rounded-lg px-3 py-2 text-sm text-sport-primary hover:bg-sport-primary/10 transition"
                          >
                            View all notifications
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Dark mode toggle — desktop only (mobile: sidebar) */}
              <button
                type="button"
                onClick={toggleDarkMode}
                className={`hidden md:inline-flex p-1.5 sm:p-2 rounded-xl transition ${iconButtonClass}`}
                aria-label={t('common.theme')}
              >
                {darkMode ? (
                  <SunIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                ) : (
                  <MoonIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>

              {/* Mobile Profile Button */}
              <button
                onClick={() => navigate(`/profile/${userData?.uid}`)}
                className={`sm:hidden p-1 rounded-xl transition ${iconButtonClass}`}
                aria-label={t('nav.profile')}
              >
                {userData?.profileImage ? (
                  <img
                    src={userData.profileImage}
                    alt="Profile"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary flex items-center justify-center text-sport-dark font-bold text-[10px]">
                    {profileInitials}
                  </div>
                )}
              </button>

              {/* User Menu */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => navigate(`/profile/${userData?.uid}`)}
                  className="flex items-center space-x-2 p-1 rounded-xl hover:bg-sport-primary/10 transition group"
                >
                  {userData?.profileImage ? (
                    <img
                      src={userData.profileImage}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover shadow-lg ring-2 ring-sport-primary/40 group-hover:shadow-sport-primary/50 transition-shadow"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary flex items-center justify-center text-sport-dark font-bold shadow-lg group-hover:shadow-sport-primary/50 transition-shadow text-xs">
                      {profileInitials}
                    </div>
                  )}
                </button>
              </div>

              {/* Language Selector — fixed box aligns with header icons */}
              <div
                ref={languageRef}
                className="relative hidden h-10 w-12 flex-shrink-0 items-center justify-center sm:flex"
              >
                <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-sport-primary/20 via-transparent to-sport-secondary/20 blur-sm" />
                <button
                  type="button"
                  onClick={() => {
                    setLanguageOpen((prev) => {
                      const willOpen = !prev
                      if (willOpen) {
                        setNotificationsOpen(false)
                      }
                      return willOpen
                    })
                  }}
                  className={`relative z-10 inline-flex h-8 w-12 shrink-0 items-center justify-center rounded-2xl border px-2 text-[10px] font-['Orbitron'] uppercase tracking-[0.12em] outline-none shadow-[0_0_18px_rgba(0,212,255,0.12)] transition hover:border-sport-primary/60 hover:shadow-[0_0_22px_rgba(0,212,255,0.18)] ${languageToggleClass}`}
                  aria-label={t('common.selectLanguage')}
                  aria-expanded={languageOpen}
                >
                  {currentLanguage === 'it' ? 'IT' : 'EN'}
                </button>

                {languageOpen && (
                  <div className={`absolute right-0 mt-2 w-36 rounded-xl p-1 shadow-2xl z-50 ${darkMode ? 'border border-sport-primary/35 bg-sport-darker/95 shadow-black/40' : 'border border-gray-200 bg-white shadow-gray-300/30'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        changeLanguage('en')
                        setLanguageOpen(false)
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left text-xs font-['Orbitron'] uppercase tracking-[0.14em] transition ${
                        currentLanguage === 'en'
                          ? 'bg-sport-primary/20 text-sport-primary'
                          : darkMode
                            ? 'text-sport-silver hover:bg-white/5 hover:text-sport-light'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        changeLanguage('it')
                        setLanguageOpen(false)
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left text-xs font-['Orbitron'] uppercase tracking-[0.14em] transition ${
                        currentLanguage === 'it'
                          ? 'bg-sport-primary/20 text-sport-primary'
                          : darkMode
                            ? 'text-sport-silver hover:bg-white/5 hover:text-sport-light'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      Italian
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className={`fixed top-0 left-0 bottom-0 z-50 flex w-72 flex-col md:hidden shadow-2xl ${sidebarClass}`}
            >
              <div className={`shrink-0 p-4 border-b ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  <img
                    src={logo}
                    alt={t('app.brand')}
                    className="h-10 w-10 object-contain drop-shadow-lg ring-glow rounded-full"
                  />
                  <span
                    className={`${brandWordmarkClass} -skew-x-6 text-lg tracking-[0.04em] truncate`}
                    style={brandWordmarkStyle}
                  >
                    {t('app.brand').toUpperCase()}
                  </span>
                </div>
              </div>
              <nav className="min-h-0 flex-1 overflow-y-auto p-4 space-y-2">
                {mobileSidebarNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition ${darkMode ? 'text-sport-silver hover:bg-sport-primary/10 hover:text-sport-primary' : 'text-gray-700 hover:bg-gray-100 hover:text-sport-primary'}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </nav>
              <div className={`shrink-0 border-t p-4 space-y-3 ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                {/* Mobile: language first, then theme (same footer strip as before) */}
                <div>
                  <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-sport-silver/60' : 'text-gray-500'}`}>{t('common.selectLanguage')}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        changeLanguage('en')
                      }}
                      className={`flex-1 rounded-lg px-3 py-2 text-xs font-['Orbitron'] uppercase transition ${
                        currentLanguage === 'en'
                          ? 'bg-sport-primary/20 text-sport-primary'
                          : darkMode
                            ? 'bg-sport-darker border border-sport-silver/20 text-sport-silver hover:border-sport-primary/50 hover:text-sport-light'
                            : 'bg-white border border-gray-300 text-gray-700 hover:border-sport-primary/50 hover:text-gray-900'
                      }`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => {
                        changeLanguage('it')
                      }}
                      className={`flex-1 rounded-lg px-3 py-2 text-xs font-['Orbitron'] uppercase transition ${
                        currentLanguage === 'it'
                          ? 'bg-sport-primary/20 text-sport-primary'
                          : darkMode
                            ? 'bg-sport-darker border border-sport-silver/20 text-sport-silver hover:border-sport-primary/50 hover:text-sport-light'
                            : 'bg-white border border-gray-300 text-gray-700 hover:border-sport-primary/50 hover:text-gray-900'
                      }`}
                    >
                      IT
                    </button>
                  </div>
                </div>

                <div className={`border-t pt-3 ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                  <div className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-sport-silver/60' : 'text-gray-500'}`}>{t('common.theme')}</div>
                  <button
                    type="button"
                    onClick={toggleDarkMode}
                    className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition ${darkMode ? 'bg-sport-primary/10 text-sport-light border border-sport-primary/25 hover:bg-sport-primary/15' : 'bg-gray-50 text-gray-800 border border-gray-200 hover:bg-gray-100'}`}
                    aria-pressed={darkMode}
                  >
                    {darkMode ? (
                      <>
                        <SunIcon className="h-5 w-5 shrink-0 text-yellow-400" />
                        <span>{t('common.light')}</span>
                      </>
                    ) : (
                      <>
                        <MoonIcon className="h-5 w-5 shrink-0" />
                        <span>{t('common.dark')}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Logout Button */}
                <button
                  onClick={() => {
                    setSidebarOpen(false)
                    handleLogout()
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span>{t('common.logout')}</span>
                </button>

                <div className={`border-t pt-3 text-center ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                  <a
                    href="mailto:giochiamo.app@gmail.com"
                    onClick={() => setSidebarOpen(false)}
                    className={`text-xs break-all transition sm:text-sm ${darkMode ? 'text-sport-silver/55 hover:text-sport-primary' : 'text-gray-500 hover:text-sport-primary'}`}
                  >
                    giochiamo.app@gmail.com
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* All Notifications Modal */}
      <AnimatePresence>
        {allNotificationsOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
              onClick={() => setAllNotificationsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="fixed inset-0 z-[61] flex items-center justify-center p-4"
            >
              <div className={`w-full max-w-2xl max-h-[80vh] shadow-2xl overflow-hidden ${modalCardClass}`}>
                <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-sport-silver/20' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold ${popoverTitleClass}`}>{t('notifications.title')}</h3>
                  <button
                    onClick={() => setAllNotificationsOpen(false)}
                    className={`p-1 rounded-lg transition ${darkMode ? 'text-sport-silver hover:text-sport-light hover:bg-sport-primary/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="max-h-[calc(80vh-4.5rem)] overflow-y-auto">
                  {notifications.length > 0 ? (
                    <div className={`divide-y ${darkMode ? 'divide-sport-silver/10' : 'divide-gray-100'}`}>
                      {notifications.map((notification) => (
                        <button
                          type="button"
                          key={`modal-${notification.source}-${notification.id}`}
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full p-3 text-left transition ${darkMode ? 'hover:bg-sport-primary/5' : 'hover:bg-gray-50'} ${notification.read ? 'opacity-80' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm break-words ${popoverTitleClass} ${notification.read ? 'font-normal' : 'font-semibold'}`}>{resolveNotificationMessage(t, notification)}</p>
                              <div className={`mt-1 flex items-center justify-between text-[11px] ${popoverMutedTextClass}`}>
                                <span>{notification.source === 'pushNotifications' ? 'Broadcast' : 'Personal'}</span>
                                <span>{formatCreatedAt(notification.createdAt)}</span>
                              </div>
                            </div>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${notification.read ? (darkMode ? 'bg-white/5 text-sport-silver/70' : 'bg-gray-100 text-gray-500') : (darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-100 text-blue-700')}`}>
                              {notification.read ? t('notifications.read', { defaultValue: 'Read' }) : t('notifications.unread', { defaultValue: 'Unread' })}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={`p-6 text-center ${popoverMutedTextClass}`}>{t('notifications.noNotifications')}</div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 min-[400px]:px-5 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  )
}

export default UserLayout
